/**
 * Gesture utilities for handling touch gestures like swipes
 * Provides reusable swipe detection functionality
 */

export default class GestureUtils {
  constructor(options = {}) {
    this.minSwipeDistance = options.minSwipeDistance || 80;
    this.maxVerticalDistance = options.maxVerticalDistance || 100;
    this.onSwipeLeftToRight = options.onSwipeLeftToRight || (() => {});
    this.onSwipeRightToLeft = options.onSwipeRightToLeft || (() => {});

    // Touch tracking variables
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;

    // Bind event handlers to preserve 'this' context
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
  }

  /**
   * Initialize swipe detection by adding event listeners to document
   */
  initSwipeDetection() {
    // Add touch event listeners to document
    document.addEventListener('touchstart', this.handleTouchStart, { passive: true });
    document.addEventListener('touchend', this.handleTouchEnd, { passive: true });
  }

  /**
   * Clean up swipe detection by removing event listeners
   */
  cleanupSwipeDetection() {
    // Remove touch event listeners from document
    document.removeEventListener('touchstart', this.handleTouchStart);
    document.removeEventListener('touchend', this.handleTouchEnd);
  }

  /**
   * Handle touch start events
   * @param {TouchEvent} event - The touchstart event
   */
  handleTouchStart(event) {
    // ignore swipe detected in simple nav which is scrollable horizontally
    if (event.target.closest('.c-mw-nav-simple')) {
      return;
    }

    if (event.target.closest('.c-mw-post-media')) {
      return;
    }

    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  /**
   * Handle touch end events
   * @param {TouchEvent} event - The touchend event
   */
  handleTouchEnd(event) {
    const touch = event.changedTouches[0];
    this.touchEndX = touch.clientX;
    this.touchEndY = touch.clientY;

    this.detectSwipe();
  }

  /**
   * Detect and handle swipe gestures
   */
  detectSwipe() {
    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Check if it's a horizontal swipe (more horizontal than vertical movement)
    if (absDeltaX > absDeltaY && absDeltaX > this.minSwipeDistance && absDeltaY < this.maxVerticalDistance) {
      // Left to right swipe
      if (deltaX > 0) {
        this.onSwipeLeftToRight(deltaX);
      }
      // Right to left swipe
      else if (deltaX < 0) {
        this.onSwipeRightToLeft(Math.abs(deltaX));
      }
    }
  }

  /**
   * Update callback functions
   * @param {Object} callbacks - Object containing callback functions
   */
  updateCallbacks(callbacks) {
    if (callbacks.onSwipeLeftToRight) {
      this.onSwipeLeftToRight = callbacks.onSwipeLeftToRight;
    }
    if (callbacks.onSwipeRightToLeft) {
      this.onSwipeRightToLeft = callbacks.onSwipeRightToLeft;
    }
  }

  /**
   * Update gesture detection parameters
   * @param {Object} options - Object containing new parameters
   */
  updateOptions(options) {
    if (options.minSwipeDistance !== undefined) {
      this.minSwipeDistance = options.minSwipeDistance;
    }
    if (options.maxVerticalDistance !== undefined) {
      this.maxVerticalDistance = options.maxVerticalDistance;
    }
  }
}
